 
import { colorStrings } from "@iewcasia/Utilities/variables";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import NavigationModeMenu, {
    NavigationModeCookieName,
    NavigationModeMenuStyles,
} from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    // VERNDALE CUSTOMIZATION
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        // VERNDALE CUSTOMIZATION
        [fields.links]: LinkModel[];
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    currentMode: getCookie(NavigationModeCookieName) || "Storefront",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasShellContext;

const Navigation = styled.div`
    height: 50px;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    /* Verndale Customization -- This customization is done here since we cannot do it on the SecondaryNavigationExtension file */

    color: ${getColor(colorStrings.textMain)};
    background-color: transparent;
    font-size: 14px;
    gap: 8px;
    padding: 0;
    text-transform: none;
    margin: 0 0 -20px;

    /* END */
`;

const NavItem = styled.span<InjectableCss>`
    display: inline-flex;

    > span,
    > a {
        margin-left: 10px;
    }
    ${injectCss}
`;

export interface SecondaryNavigationStyles {
    wrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    showInventoryLink?: InjectableCss;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingLink?: InjectableCss;
    showPricingToggle?: ShowPricingToggleStyles;
    currencyLink?: InjectableCss;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
    // Verndale Customization
    navigationModeMenuLinks?: InjectableCss;
    link?: LinkPresentationProps;
    countryLinks?: InjectableCss;
}

export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    showInventoryLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    showPricingLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    currencyLink: {
        css: css`
            margin-left: 16px;
        `,
    },
    languageLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
        `,
    },
    warehousePickupLink: {
        css: css`
            flex: 0 1 auto;
            margin-left: 6px;
            min-width: 30px;
        `,
    },
    signInLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 6px;
        `,
    },
    countryLinks: {
        css: css`
            margin-left: 6px;
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.navigation-mode-menu {
}

.currency-menu {
}

.language-menu {
}

.ship-to-address-menu {
}

.sign-in-link {
}

.country-selector-container {
}`;

const SecondaryNavigation: FC<Props> = ({
    id,
    enableWarehousePickup,
    displayPricingAndInventory,
    currentMode,
    fields,
    shellContext: { isEditing },
}) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);

    //  VERNDALE CUSTOMIZATION
    const links = useGetLinks(fields.links, o => o.fields.destination);
    // END VERNDALE CUSTOMIZATION

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <Hidden below="lg">
                <Navigation className="wrapper" {...styles.wrapper}>
                    <NavItem className="navigation-list-link" {...styles.navigationModeMenuLinks}>
                        {/* VERNDALE CUSTOMIZATION */}
                        {Array.isArray(links.links) &&
                            links.links.length > 0 &&   
                            links.links.map((link, index) => (
                                <Link
                                    key={`${link.url}.${link.title}.${index}`}
                                    className="list-link"
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    {...styles.link}
                                >
                                    {fields.links[index].fields.overriddenTitle
                                        ? fields.links[index].fields.overriddenTitle
                                        : link.title}
                                </Link>
                            ))}
                    </NavItem>
                    {/* END VERNDALE CUSTOMIZATION */}

                    <NavItem className="navigation-mode-menu">
                        <NavigationModeMenu extendedStyles={styles.navigationModeMenu} />
                    </NavItem>
                    {fields.showInventoryToggle && displayPricingAndInventory && currentMode === "Storefront" && (
                        <NavItem className="show-inventory-toggle" {...styles.showInventoryLink}>
                            <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                        </NavItem>
                    )}
                    {fields.showPricingToggle && displayPricingAndInventory && currentMode === "Storefront" && (
                        <NavItem className="show-pricing-toggle" {...styles.showPricingLink}>
                            <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                        </NavItem>
                    )}
                    <NavItem className="sign-in-link" {...styles.signInLink}>
                        <Zone zoneName="SignIn" contentId={id} fixed />
                    </NavItem>
                    <NavItem className="currency-menu" {...styles.currencyLink}>
                        <Zone zoneName="Currency" contentId={id} fixed />
                    </NavItem>
                    <NavItem className="language-menu" {...styles.languageLink}>
                        <Zone zoneName="Language" contentId={id} fixed />
                    </NavItem>
                    {enableWarehousePickup && (
                        <NavItem className="ship-to-address-menu" {...styles.warehousePickupLink}>
                            <Zone zoneName="ShipToAddress" contentId={id} fixed />
                        </NavItem>
                    )}
                    <NavItem
                        className="country-selector-container"
                        {...styles.countryLinks}
                        style={isEditing ? { minWidth: "50px", minHeight: "50px" } : {}}
                    >
                        <Zone zoneName="CountrySelector" contentId={id} />
                    </NavItem>
                </Navigation>
            </Hidden>
        </StyledWrapper>
    );
};

// Verndale Customization
const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};
// END Verndale Customization

const secondaryNavigation: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(SecondaryNavigation) as any),
    definition: {
        group: "Common",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            // Verndale Customization
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;

                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
                tab: basicTab,
            },
            // END Verndale Customization
        ],
    },
};

export default secondaryNavigation;
